import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { YoutubeVideoPreviewData } from '../../../../../../../models/google-template/preview-interfaces/youtube-video-preview-data.interface';
import { UrlDisplayHelper } from '../../../../../../../utils/url-display-helper';
import { YoutubeFormatHelper } from '../../../../../../../utils/youtube-format-helper';
import { DesktopFrameComponent } from '../../../preview-common/desktop-frame/desktop-frame.component';
import { YoutubeNavbarComponent } from '../../../preview-common/youtube/youtube-navbar/youtube-navbar.component';

/**
 * YouTube in-stream placement on the desktop watch page. The video runs in the
 * player with the advertiser card over its bottom-left corner, and the same
 * video is shown again as the companion banner at the top of the sidebar. The
 * player is dark and the video is contained in it, so a video of any format is
 * shown whole, pillarboxed where it is not 16:9.
 */
@Component({
    selector: 'app-youtube-video-in-stream-desktop',
    imports: [CommonModule, DesktopFrameComponent, YoutubeNavbarComponent],
    templateUrl: './youtube-in-stream-desktop.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './youtube-in-stream-desktop.component.scss'
})
export class YoutubeVideoInStreamDesktopPreviewComponent {
  @Input() data!: YoutubeVideoPreviewData;

  /**
   * Stubbed rows for the sidebar's up-next list and the comments under the
   * player. More than the full-width window shows: the page scales with the
   * window, so a narrower one reaches further down the page.
   */
  readonly upNextRows = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  readonly commentRows = [0, 1, 2, 3, 4, 5];

  /** Thumbnail of the video, or '' when there isn't one yet. */
  get thumbnailUrl(): string {
    return YoutubeFormatHelper.thumbnailFor(this.videoId);
  }

  /**
   * True while the video's format is still being measured, so the empty player
   * says so rather than asking for a video that is about to turn up.
   */
  get isResolving(): boolean {
    return YoutubeFormatHelper.isResolving(this.videoId);
  }

  /** The host, which is all either card shows of the destination. */
  get displayUrl(): string {
    return UrlDisplayHelper.hostOf(this.data.url);
  }

  /** Both cards give the headline one line. */
  get headline(): string {
    return this.data.headline || 'Your headline appears here';
  }

  get ctaText(): string {
    return this.data.ctaText || 'Your call to action';
  }

  private get videoId(): string {
    return this.data.video?.videoId ?? '';
  }
}
